import { useMemo } from "react";

import { useGetClientAssessments } from "hooks/api/useGetClientAssessements";
import { useGetV3ClientAssessments } from "hooks/api/useGetV3ClientAssessments";
import { ASSESSMENT_DEFINITIONS, getAssessmentRecordsByType } from "../assessmentConfig";

import { normalizeV3Records } from "./assessmentDataTransforms";
import type { AssessmentRecord, AssessmentType } from "../types";

export type AssessmentCardState = "ready" | "noData" | "error";

export interface AssessmentCardStatus {
  state: AssessmentCardState;
  refetch: () => Promise<unknown>;
}

export function useRawAssessments(clientId?: string) {
  const {
    clientAssessments: vinelandAssessments,
    assessmentsLoading: vinelandLoading,
    isError: vinelandError,
    error: vinelandErrorDetails,
    refetch: refetchVineland,
  } = useGetClientAssessments(clientId);

  const {
    rawDataByType,
    queryStatusByType,
    isLoading: v3Loading,
    isError: v3Error,
  } = useGetV3ClientAssessments(clientId);

  const v3NormalizedRecords = useMemo<AssessmentRecord[]>(() => {
    return Object.entries(rawDataByType).flatMap(([type, rawItems]) =>
      normalizeV3Records(rawItems ?? [], type as AssessmentType)
    );
  }, [rawDataByType]);

  const rawAssessments = useMemo<AssessmentRecord[]>(() => {
    const vineland = (vinelandAssessments as AssessmentRecord[] | null) ?? [];
    return [...vineland, ...v3NormalizedRecords];
  }, [vinelandAssessments, v3NormalizedRecords]);

  const cardStatusByType = useMemo<Partial<Record<AssessmentType, AssessmentCardStatus>>>(() => {
    const byType = getAssessmentRecordsByType(rawAssessments);
    const vinelandStatusCode = (
      vinelandErrorDetails as { response?: { status?: number } } | undefined
    )?.response?.status;

    return ASSESSMENT_DEFINITIONS.reduce(
      (acc, definition) => {
        const type = definition.type;
        const hasData = (byType[type] || []).length > 0;

        if (type === "vineland") {
          if (hasData) {
            acc[type] = { state: "ready", refetch: refetchVineland };
            return acc;
          }

          if (vinelandError) {
            acc[type] = {
              state: vinelandStatusCode === 404 ? "noData" : "error",
              refetch: refetchVineland,
            };
            return acc;
          }

          acc[type] = { state: "noData", refetch: refetchVineland };
          return acc;
        }

        const queryStatus = queryStatusByType[type];
        if (hasData) {
          acc[type] = { state: "ready", refetch: queryStatus?.refetch || (async () => undefined) };
          return acc;
        }

        if (queryStatus?.isError) {
          acc[type] = {
            state: queryStatus.isNotFound ? "noData" : "error",
            refetch: queryStatus.refetch,
          };
          return acc;
        }

        acc[type] = { state: "noData", refetch: queryStatus?.refetch || (async () => undefined) };
        return acc;
      },
      {} as Partial<Record<AssessmentType, AssessmentCardStatus>>
    );
  }, [queryStatusByType, rawAssessments, refetchVineland, vinelandError, vinelandErrorDetails]);

  return {
    rawAssessments,
    assessmentsLoading: vinelandLoading || v3Loading,
    isError: vinelandError && v3Error,
    cardStatusByType,
  };
}
